import { useForm } from "react-hook-form";
import { CBInput, CBSelect } from "../../shared";
import { CBHandMessage } from "../../shared/CBHandMessage/CBHandMessage";
import CustomCard from "../../shared/CustomCard/CustomCard";
import { yupResolver } from "@hookform/resolvers/yup";
import { profileObj, userProfileSchema, userProfileValues } from "../utils";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../context/user.context";
import {
  Button,
  ConfigProvider,
  Radio,
  RadioChangeEvent,
  Typography,
} from "antd";
import { designationsOptions } from "../../../constants/designations.constants";
import {
  CitiesOpts,
  CountriesOpts,
  StatesOpts,
} from "../../../constants/countries.constants";
import { CheckOutlined } from "@ant-design/icons";
import User from "../../../interfaces/user.interface";
import {
  getUserDetails,
  updateUser as updateUserDto,
} from "../../../services/users.service";
import { CheckboxGroupProps } from "antd/es/checkbox";
import Message from "./../../shared/Message/Message";
import { useRole } from "../../../hooks/user-roles";
import { employmentOptions } from "../../../constants/employment.constants";

interface IProfile {
  userId?: string;
}
interface ListsI {
  value: string;
  label: string;
  code: string;
}

const Profile = ({ userId }: IProfile) => {
  const [country, setCountry] = useState<string>("0");
  const [countryCode, setCountryCode] = useState<ListsI>();
  const [userFound, setUserFound] = useState<User | undefined>();
  const [state, setState] = useState<string>("");
  const { user, updateUser } = useContext(UserContext);
  const { isReadOnly } = useRole();
  // Directors may edit their own profile, but only view other users'.
  const viewOnly = isReadOnly && !!userId && Number(userId) !== user?.id;
  const { Text } = Typography;
  const countries: ListsI[] = CountriesOpts();
  const [states, setStates] = useState<ListsI[]>([]);
  const [cities, setCities] = useState<ListsI[]>([]);
  const { control, watch, handleSubmit, setValue } = useForm({
    mode: "onBlur",
    resolver: yupResolver(userProfileSchema),
    values: userProfileValues(userId ? userFound! : user!),
    defaultValues: { title: "mr" },
  });

  const onChangeTitle = (e: RadioChangeEvent) =>
    setValue("title", e.target.value, { shouldDirty: true });

  const changeCountry = (val: string) => {
    const element = countries.find((item) => {
      return item.value === val;
    });
    setCountryCode(element);
    setCountry(element?.code ?? "");

    const newStatesOps = StatesOpts(element?.code ?? "");
    setStates(newStatesOps);
    setState("");

    setCities([]);

    setValue("province", newStatesOps.length ? newStatesOps[0].value : "");
    setValue("city", "");
  };

  const changeState = (val: string) => {
    const element = states.find((item) => {
      return item.value === val;
    });
    setState(element?.code ?? "");
    setCities(CitiesOpts(country, element?.code ?? ""));
  };

  const submitHandler = async () => {
    const profile: User = profileObj(userId ? userFound! : user!, watch());

    try {
      await updateUserDto(profile);
      if (!userId) updateUser(profile);
      Message().success(`The update SEABC Profile was successfully`);
    } catch (error) {
      Message().error(`The update SEABC Profile was not successfully`);
    }
  };

  const getUserInfo = async () => setUserFound(await getUserDetails(userId!));
  const options: CheckboxGroupProps<string>["options"] = [
    { label: "Mr.", value: "mr" },
    { label: "Ms.", value: "ms" },
    { label: "Dr.", value: "dr" },
  ];
  useEffect(() => {
    if (userId) getUserInfo();
  }, [userId]);

  useEffect(() => {
    if (user) {
      setCountry(user.country);
      setState(user.province);
      const countryObj: ListsI[] = countries.filter((item) => {
        return item.value === user.country;
      });
      setCountryCode(countryObj[0]);
      setStates(StatesOpts(countryObj[0]?.code));
    }
  }, [user]);

  useEffect(() => {
    if (states.length && countryCode) {
      const provinceObj: ListsI[] = states.filter((item) => {
        return item.value === user?.province;
      });
      setCities(CitiesOpts(countryCode.code, provinceObj[0]?.code));
    }
  }, [states]);

  return (
    <>
      <div className="flex justify-center">
        {!userId && user?.role === "admin" && (
          <CBHandMessage color={"yellow"} extended>
            {'This will be the "general enquiries" contact info'}
          </CBHandMessage>
        )}
      </div>
      <form onSubmit={handleSubmit(submitHandler)}>
        <ConfigProvider componentDisabled={viewOnly}>
          <div
            className="flex gap-8 max-w-6xl pt-4"
            style={{ margin: "0 auto" }}
          >
            <CustomCard title="Contact Info">
              <div className="flex flex-col p-4">
                <CBInput
                  className="flex-1"
                  control={control}
                  type="email"
                  label="Email"
                  name="email"
                  required
                  disabled
                />
                <CBInput
                  className="flex-1"
                  control={control}
                  type="email"
                  label="Contact Email"
                  name="contactEmail"
                  required
                  disabled={viewOnly}
                />
                <div
                  style={{
                    maxWidth: "23rem",
                    fontWeight: 400,
                    color: "rgba(0, 0, 0, 0.25)",
                  }}
                >
                  Your "Contact Email" Will be used by the administrator of the
                  course to contact you if necessary.
                </div>
                <CBInput
                  className="flex-1"
                  control={control}
                  type="phone"
                  label="Phone"
                  name="phone"
                  required
                  disabled={viewOnly}
                />
              </div>
            </CustomCard>
            <CustomCard title="Personal Info">
              <div className="p-4">
                <div className="flex gap-5 pt-5 pb-2 mr-5">
                  <Text>Title:</Text>
                  <Radio.Group
                    onChange={onChangeTitle}
                    value={watch("title")}
                    className="flex gap-5"
                    options={options}
                  />
                </div>
                <div className="flex gap-8">
                  <CBSelect
                    className="flex-none w-24"
                    control={control}
                    label="Designation:"
                    name="designation"
                    required
                    defaultValue={"No"}
                    options={designationsOptions}
                    disabled={viewOnly}
                  />
                  <CBInput
                    className="flex-1"
                    control={control}
                    type="text"
                    label="Last Name:"
                    name="lastName"
                    required
                    disabled={viewOnly}
                  />
                  <CBInput
                    className="flex-1"
                    control={control}
                    type="text"
                    label="First Name: "
                    name="firstName"
                    required
                    disabled={viewOnly}
                  />
                  <CBInput
                    control={control}
                    label="Year obtained bachelor’s degree:"
                    name="degreeYear"
                    required
                    disabled={viewOnly}
                  />
                </div>
                <div className="flex gap-8">
                  <CBSelect
                    className="flex-none w-30"
                    control={control}
                    label="SEABC Member:"
                    name="member"
                    required
                    defaultValue={"No"}
                    options={[
                      { value: "1", label: "Yes" },
                      { value: "0", label: "No" },
                    ]}
                    disabled={viewOnly}
                  />
                  {watch("member") === "1" && (
                    <CBInput
                      control={control}
                      label="Membership number:"
                      name="membership"
                      required
                      disabled={viewOnly}
                    />
                  )}
                  <CBSelect
                    className="flex-none w-38"
                    control={control}
                    label="Employment status:"
                    name="employmentStatus"
                    required
                    defaultValue={"Employed"}
                    options={employmentOptions}
                    disabled={viewOnly}
                  />
                  {watch("employmentStatus") === "2" && (
                    <CBInput
                      className="w-56"
                      type="text"
                      control={control}
                      label="Name of firm:"
                      name="employer"
                      required
                      disabled={viewOnly}
                    />
                  )}
                </div>
                <div className="flex gap-6">
                  <CBInput
                    className="flex-none w-24"
                    control={control}
                    type="text"
                    label="Postal Code:"
                    name="zipCode"
                    required
                    disabled={viewOnly}
                  />
                  <CBSelect
                    className="flex-none w-44"
                    searchable={true}
                    control={control}
                    label="Country:"
                    name="country"
                    required
                    defaultValue={"No"}
                    options={countries}
                    onChange={changeCountry}
                    disabled={viewOnly}
                  />
                  {states.length ? (
                    <CBSelect
                      searchable={true}
                      className="flex-none w-40"
                      control={control}
                      label="Province:"
                      name="province"
                      required
                      defaultValue={"No"}
                      options={states}
                      onChange={changeState}
                      disabled={
                        viewOnly ||
                        (country !== "" && country !== "0" ? false : true)
                      }
                    />
                  ) : (
                    <CBInput
                      className="flex-1 w-40"
                      control={control}
                      type="text"
                      label="Province"
                      name="province"
                      disabled={viewOnly || (country !== "0" ? false : true)}
                    />
                  )}
                  {cities.length ? (
                    <CBSelect
                      searchable={true}
                      control={control}
                      label="City:"
                      name="city"
                      required
                      className="flex-none w-40"
                      defaultValue={"No"}
                      options={cities}
                      disabled={
                        viewOnly ||
                        (country !== "" && country !== "0" && state !== ""
                          ? false
                          : true)
                      }
                    />
                  ) : (
                    <CBInput
                      className="flex-1 w-40"
                      control={control}
                      type="text"
                      label="City"
                      name="city"
                      required
                      disabled={
                        viewOnly ||
                        (country !== "0" &&
                        (state !== "" || states.length === 0)
                          ? false
                          : true)
                      }
                    />
                  )}
                </div>
                <div className="flex gap-8">
                  <CBInput
                    className="flex-none w-32"
                    control={control}
                    type="text"
                    label="Apt./Suite:"
                    name="aptSuite"
                    disabled={viewOnly}
                  />
                  <CBInput
                    className="flex-1"
                    control={control}
                    type="text"
                    label="Address"
                    name="address"
                    required
                    disabled={viewOnly}
                  />
                </div>
              </div>
            </CustomCard>
          </div>
        </ConfigProvider>
        {!viewOnly && (
          <div className="flex justify-center">
            <Button
              type="primary"
              htmlType="submit"
              icon={<CheckOutlined />}
              className="w-48"
            >
              Save
            </Button>
          </div>
        )}
      </form>
    </>
  );
};

export default Profile;
